import React, { Fragment, useCallback } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import isError from 'lodash/isError';
import trim from 'lodash/trim';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';

// GLOBAL FUNCTIONS
import { controlName, doCallback, makePath, sleep } from 'functions.js';
import { recountActions } from 'actions.js';

// HOOKS/CONTEXT
import { useListData } from 'helpers/getListData';
import { getEpostData, useEpostContext } from 'helpers/getEpostData';
import { useRecordContext } from 'helpers/getRecordData';

// CORE COMPONENTS
import Form from 'core/form/Form';
import Sprite from 'core/tools/Sprite';

// PARTS
import Sidebar from 'parts/sidebar/Sidebar';

// LOCAL COMPONENTS
import EpostAttachment from './EpostAttachment';

// STYLES
import styles from './epostSidebar.module.scss';

// MAIN COMPONENT
const RebillEpostSidebar = ({ recordID, onSuccess, onClose }) => {

  // REDUX
  const user = useSelector(state => state.user);
  const dispatch = useDispatch();

  // HOOKS
  const { epostData } = useEpostContext();

  // CALLBACKS
  const handleSuccess = useCallback(
    async () => {
      await sleep(2000);
      doCallback(onSuccess);
      dispatch(recountActions.toggle());
    }, 
    [onSuccess, dispatch]
  );

  // RENDER
  return (
    <Sidebar
      name="epostSidebar"
      title="Rebill E-Post Deal Package to Accounting"
      onClose={onClose}
    >{!epostData ? (
      <div className="d-flex align-items-center mt-3">
        <Sprite.Loader className="me-2" />
        <span className="loading text-muted font-italic large">Loading E-Post data</span>
      </div>
    ) : isError(epostData) ? (
      <div className="d-flex align-items-center mt-3">
        <Sprite className="me-2" as={false} use="warning" fill="danger" />
        <div className="text-danger large">Unable to load E-Post data.</div>
      </div>
    ) : (
      <Sidebar.Form
        method="PUT"
        endpoint={makePath(ENDPOINTS.deliveryLog.epost, recordID)}
        loadingMessage="Submitting E-Post"
        errorMessage="Unable to submit E-Post."
        successMessage="E-Post submitted successfully."
        onSuccess={handleSuccess}
        buttons={{
          submit: {
            variant: 'success',
            label: 'E-Post',
            icon: 'checkmark'
          }
        }}
      >
        {({ values: { dummy } }) => [
          <Form.PlainText
            label={{
              className: styles.infoLabel,
              children: !!epostData.rebill_requested ? 'Rebill Correction Request Date' : 'Date of Rebill',
              col: {
                className: styles.infoLabelCol
              }
            }}
            value={
              !!epostData.rebill_requested
                ? `${epostData.rebill_request_date} at ${epostData.rebill_request_time}`
                : epostData.date_updated
            }
            cols={{
              xs: 24,
              md: 12
            }}
          />,
          <Form.PlainText
            label={{
              className: styles.infoLabel,
              children: 'Rebill by',
              col: {
                className: styles.infoLabelCol
              }
            }}
            value={epostData.updated_by}
            cols={{
              xs: 24,
              md: 12
            }}
          />,
          !!epostData.rebill_requested && (
            <Form.PlainText
              label={{
                className: styles.infoLabel,
                children: 'Comments',
                col: {
                  className: styles.infoLabelCol
                }
              }}
              value={epostData.rebill_request_comment}
            />
          ),
          <Form.Divider />,
          !epostData.rebill_requested && [
            <>
              <Form.Label label="The following item(s) were changed in this rebill:" plaintext />
            </>,
            <Form.Label
              label="Field"
              className={styles.infoLabel}
              col={{ className: styles.infoLabelCol }}
              vertical
              cols={{ sm: 8, lg: 10, xs: 24 }}
            />,
            <Form.Label
              label="Original Value"
              className={styles.infoLabel}
              col={{ className: styles.infoLabelCol }}
              vertical
              cols={{ sm: 8, lg: 7, xs: 24 }}
            />,
            <Form.Label
              label="New Value"
              className={styles.infoLabel}
              col={{ className: styles.infoLabelCol }}
              vertical
              cols={{ sm: 8, lg: 7, xs: 24 }}
            />,
            (epostData.aryRebillEdit?.length ? epostData.aryRebillEdit : [{}]).map(
              ({ field, orig_value, new_value }, i) => (
                <Fragment key={i}>
                  <Form.PlainText value={trim(field)} cols={{ sm: 8, lg: 10, xs: 24 }} />
                  <Form.PlainText value={trim(orig_value)} cols={{ sm: 8, lg: 7, xs: 24 }} />
                  <Form.PlainText
                    className={clsx(!!trim(new_value) && styles.newValue)}
                    value={trim(new_value)}
                    cols={{ sm: 8, lg: 7, xs: 24 }}
                  />
                </Fragment>
              )
            )
          ],
          !!epostData.aryRebillEdit?.length && <Form.Divider />,
          (!!epostData.aryRebillEdit?.length || !!epostData.rebill_requested) && (
            <Form.YesNo
              name={controlName('requiresChanges', 'dummy')}
              label={{
                label: `Did the deal edit(s) listed above require a change to the Buyer's Order, Bank Contract and/or Product Contract?`,
                vertical: true
              }}
              vertical={false}
              required
            />
          ),
          !!parseInt(dummy?.requiresChanges) && <Form.Divider />,
          !!parseInt(dummy?.requiresChanges) && (
            <EpostAttachment
              label="Please upload the revised Executed Deal Package."
              name="packageAttachment"
              documentType={epostData.ebp_salesattachment_type_id}
              sales_id={recordID}
            />
          ),
          <Form.Divider />,
          <Form.YesNo
            name={controlName('changesVerified', 'dummy')}
            type="checkbox"
            text={`I, ${user.first_name} ${user.last_name}, have verified that the revised gross recap and the uploaded Accounting Invoice / Bill of Sale match.`}
            requiredTrue
          />,
          <Form.Divider />,
          <EpostAttachment
            label="Please upload the revised, signed Accounting Invoice / Bill of Sale for this change."
            name="invoiceAttachment"
            documentType={epostData.ebos_salesattachment_type_id}
            disabled={!dummy?.changesVerified}
            sales_id={recordID}
          />
        ]}
      </Sidebar.Form>
    )}</Sidebar>
  );
};
RebillEpostSidebar.Button = props => (
  <Sidebar.Button
    name="epostSidebar"
    {...props}
  />
)
const RebillEpostSidebarWithContext = getEpostData(props => {

  // HOOKS
  const { clearData } = useEpostContext();

  // RENDER
  return <RebillEpostSidebar onClose={() => clearData} {...props} />
});
const ListViewRebillEpostSidebar = props => {
    
  // REDUX
  const recordID = useSelector(state => state.sidebar.epostSidebar);

  // HOOKS
  const { updateContext } = useListData();

  // CALLBACKS
  const handleSuccess = useCallback(
    () => {
      updateContext({ type: 'updateSettings', reload: true });
    },
    [updateContext]
  )

  // RENDER
  return (
    <RebillEpostSidebarWithContext recordID={recordID} onSuccess={handleSuccess} {...props} />
  )
};
RebillEpostSidebar.List = ListViewRebillEpostSidebar;
const RecordViewRebillEpostSidebar = props => {

  // REACT ROUTER
  const { recordID } = useParams();

  // HOOKS
  const { refetchRecord } = useRecordContext();

  // CALLBACKS
  const handleSuccess = useCallback(
    () => {
      refetchRecord();
    },
    [refetchRecord]
  );

  // RENDER
  return createPortal(
    <RebillEpostSidebar recordID={recordID} onSuccess={handleSuccess} {...props} />,
    document.body
  )
}
RebillEpostSidebar.Record = RecordViewRebillEpostSidebar;

export default RebillEpostSidebar;
